import {
    AvailablePlanType,
    CalculatorResultType,
} from "@common-shared/config/coverage.config"
import {
    FeatureFlag,
    FeatureFlagsExistenceLogicalOperator,
} from "@common-shared/config/featureFlags.config.ts"
import { COMMON_ONE_VERIFY_CHECKER_CONFIG } from "@common-shared/config/oneVerify/oneVerifyChecker.config.ts"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import {
    PoliciesListType,
    PolicyModalityCode,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { IOVCoverageResultDemographics } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import type {
    ICheckerRunningTypeTab,
    ICheckerTypeTab,
} from "../types/objects.types.ts"
import { FEATURE_FLAGS_CONFIG } from "./featureFlags.config"
import { GENERAL_CONFIG } from "./general.config"

import aetnaIcon from "@front-shared/assets/images/payers/aetna.svg"
import cignaIcon from "@front-shared/assets/images/payers/cigna.svg"
import humanaIcon from "@front-shared/assets/images/payers/humana.svg"
import tuftsIcon from "@front-shared/assets/images/payers/tufts.svg"

export enum CoverageCheckerMode {
    MANUAL = "manual",
    CSV = "csv",
}

export enum CoverageCheckerRunningState {
    INPUT_MANUAL = "INPUT_MANUAL",
    INPUT_CSV = "INPUT_CSV",
    PROCESS_CSV = "PROCESS_CSV",
    PREVIEW_CSV = "PREVIEW_CSV",
    RESULTS_MANUAL = "RESULTS_MANUAL",
    RESULTS_CSV = "RESULTS_CSV",
}

export const CHECKER_CONFIG = {
    ...COMMON_ONE_VERIFY_CHECKER_CONFIG,

    maxGetEstimateRetry: 3,

    maxBulkCoverageChecks: 10, // 10 at a time checks

    // How many checks we process at once
    processBatchSize: 10,
    processBatchSizeExtended: 25,

    get inNetworkOnlyCheckTypes(): QuotaUsageType[] {
        return [
            QuotaUsageType.MEDICAID,
            QuotaUsageType.AGENTIC_PORTAL,
            QuotaUsageType.AGENTIC_PHONE,
        ]
    },

    get checkerRunningTypeTabs(): ICheckerRunningTypeTab[] {
        return [
            {
                key: CoverageCheckerMode.MANUAL,
                label: "Single Patient",
                featureFlag: FEATURE_FLAGS_CONFIG.manualCheckerFeatureFlags,
                featureFlagExistenceOperator:
                    FeatureFlagsExistenceLogicalOperator.OR,
            },
            {
                key: CoverageCheckerMode.CSV,
                label: "Multiple Patients",
                featureFlag: FEATURE_FLAGS_CONFIG.bulkCheckerFeatureFlags,
                featureFlagExistenceOperator:
                    FeatureFlagsExistenceLogicalOperator.OR,
            },
        ]
    },

    get manualTypeTabs(): ICheckerTypeTab[] {
        return [
            {
                key: QuotaUsageType.GENERAL,
                label: "Standard",
                featureFlag: FeatureFlag.ONE_VERIFY_STANDARD_MANUAL_CHECK,
            },
            {
                key: QuotaUsageType.DISCOVER,
                label: "Discover",
                featureFlag: FeatureFlag.ONE_VERIFY_DISCOVER_MANUAL_CHECK,
            },
            {
                key: QuotaUsageType.SMART_SCAN,
                label: "Smart Scan",
                featureFlag: FeatureFlag.ONE_VERIFY_SCAN_MANUAL_CHECK,
            },
            {
                key: QuotaUsageType.MEDICAID,
                label: "Medicaid",
                featureFlag: FeatureFlag.ONE_VERIFY_MEDICAID_MANUAL_CHECK,
            },
            {
                key: QuotaUsageType.AGENTIC_PHONE,
                label: "Agentic",
                featureFlag: FeatureFlag.ONE_VERIFY_AGENTIC_PHONE_MANUAL_CHECK,
                limitedToModalityCodes: [
                    PolicyModalityCode.IOP,
                    PolicyModalityCode.PHP,
                ],
            },
        ]
    },

    get demographicsDifference(): Record<
        "inputRequiredKeys" | "ignoreKeys",
        (keyof IOVCoverageResultDemographics)[]
    > {
        return {
            inputRequiredKeys: ["memberId"],
            ignoreKeys: ["gender", "address"],
        }
    },

    get planTypeMappingByViewType(): Record<
        PoliciesListType,
        Partial<Record<AvailablePlanType, string>>
    > {
        return {
            [PoliciesListType.MEDICAID]: {
                [AvailablePlanType.ORIGINAL_MEDICAID]:
                    CHECKER_CONFIG.planTypeMapping[
                        AvailablePlanType.ORIGINAL_MEDICAID
                    ],
                [AvailablePlanType.MANAGED_MEDICAID]:
                    CHECKER_CONFIG.planTypeMapping[
                        AvailablePlanType.MANAGED_MEDICAID
                    ],
                [AvailablePlanType.NOT_APPLICABLE]:
                    CHECKER_CONFIG.planTypeMapping[
                        AvailablePlanType.NOT_APPLICABLE
                    ],
            },
            [PoliciesListType.HISTORY_SEARCH]: this.planTypeMapping,
            [PoliciesListType.OVERRIDES]: this.planTypeMapping,
            [PoliciesListType.PLAN_YEAR_RESETS]: this.planTypeMapping,
            [PoliciesListType.REPORTS]: this.planTypeMapping,
            [PoliciesListType.AGENTIC]: this.planTypeMapping,
            [PoliciesListType.ASSIGNED_TASKS]: this.planTypeMapping,
            [PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS]:
                this.planTypeMapping,
            [PoliciesListType.TASK_ASSIGNMENTS]: this.planTypeMapping,
            [PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS]:
                this.planTypeMapping,
            [PoliciesListType.ASSIGNED_TASKS_MEDICAID]: this.planTypeMapping,
            [PoliciesListType.TASK_ASSIGNMENTS_MEDICAID]: this.planTypeMapping,
        }
    },

    get calculatorResultTypeColorMapping(): Record<
        CalculatorResultType,
        string
    > {
        return {
            [CalculatorResultType.activeCoverage]: "#7064EA",
            [CalculatorResultType.activePartialCoverage]: "#AE9BEA",

            [CalculatorResultType.noCoverage]: "#2F1D47",

            [CalculatorResultType.activePlanCoverageUnknown]: "#800000",
            [CalculatorResultType.unknownPlanStatus]: "#761919",
            [CalculatorResultType.generalError]: "#B22222",
            [CalculatorResultType.memberNotFound]: "#BC2626",
            [CalculatorResultType.connectionIssue]: "#A45A52",
            [CalculatorResultType.payerNotSupported]: "#EA3C53",
            [CalculatorResultType.providerNotRecognized]: "#D21F3C",
            [CalculatorResultType.errorRetrievingCoverage]: "#420D09",
            [CalculatorResultType.memberIdPayerIdInvalid]: "#5E1914",
            [CalculatorResultType.invalidInput]: "#FFA07A",
            [CalculatorResultType.unexpectedError]: "#E9967A",
            [CalculatorResultType.tooManyRequests]: "#DB7093",
            [CalculatorResultType.payerFailure]: "#BA1607",
        }
    },

    get quickResultsSuccessfulResults(): CalculatorResultType[] {
        return [
            ...CHECKER_CONFIG.activeCoverageResults,

            CalculatorResultType.noCoverage,
        ]
    },

    get quickViewExcludedResultTypes(): CalculatorResultType[] {
        return [CalculatorResultType.activePlanCoverageUnknown]
    },

    get supportedPayersUrl(): Record<QuotaUsageType, string | null> {
        return {
            [QuotaUsageType.GENERAL]: GENERAL_CONFIG.supportedInsurersUrl,
            [QuotaUsageType.DISCOVER]:
                GENERAL_CONFIG.supportedInsurersDiscoverUrl,
            [QuotaUsageType.SMART_SCAN]: null,
            [QuotaUsageType.MEDICAID]: null,
            [QuotaUsageType.AGENTIC_PORTAL]:
                GENERAL_CONFIG.supportedInsurersAgenticUrl,
            [QuotaUsageType.AGENTIC_PHONE]:
                GENERAL_CONFIG.supportedInsurersAgenticUrl,
        }
    },

    get payerIconByExternalId(): Record<string, string> {
        return {
            "60054": aetnaIcon,
            "62308": cignaIcon,
            HUMANA: humanaIcon,
            "04298": tuftsIcon,
        }
    },
}
